import { getItemDef } from "./items";
import { jitterAngle } from "./physics/config";
import {
  cardinalVec,
  effectiveCells,
  rotateCardinal,
  shipCellSize,
  shipExtent,
} from "./ship";
import { sparkHeatCapJPerK } from "./sparks";
import { SPECIFIC_HEAT_J_PER_KG_K } from "./thermal";
import type {
  Ship,
  ShipModule,
  Spark,
  SpaceItem,
  World,
} from "./types";

const BASE_RAYS = 32;
const CONE_HALF_ANGLE = 0.35;
const RAY_WIDTH_M = 2.4;

/** Mirrors tiles.wgsl PLUME_FALLOFF_EXP. */
const PLUME_FALLOFF_EXP = 2.0;

const CHILD_LEN_FRAC = 0.45;
const MIN_ACTIVATION = 0.01;

/** Matches physics/step.ts SPARK_RADIUS_M. */
const SPARK_HIT_RADIUS_M = 0.25;

interface RayHit {
  t: number;
  nx: number;
  ny: number;
  kind: "item" | "module" | "spark";
  item?: SpaceItem;
  ship?: Ship;
  module?: ShipModule;
  spark?: Spark;
}

const _candItems: SpaceItem[] = [];
const _candShips: Ship[] = [];
const _candSparks: Spark[] = [];
const _scratchHit: RayHit = { t: 0, nx: 0, ny: 0, kind: "item" };

// Broadphase for the per-port plume candidate gather. Item/spark positions are
// fixed for the duration of stepPlumes (applyHit only changes vel/temp), so we
// hash them once per tick and query by plume AABB instead of rescanning the
// whole world per active thruster port. castRay re-tests every candidate with
// rayAabbEntry, so a superset is sufficient — results are identical.
const POINT_CELL_M = 32;
function pointCellKey(cx: number, cy: number): number {
  return ((cx & 0xffff) << 16) | (cy & 0xffff);
}

class PointHash<T extends { pos: { x: number; y: number } }> {
  private cells = new Map<number, T[]>();
  private pool: T[][] = [];

  clear(): void {
    for (const a of this.cells.values()) {
      a.length = 0;
      this.pool.push(a);
    }
    this.cells.clear();
  }

  insert(o: T): void {
    const k = pointCellKey(
      Math.floor(o.pos.x / POINT_CELL_M),
      Math.floor(o.pos.y / POINT_CELL_M),
    );
    let a = this.cells.get(k);
    if (a === undefined) {
      a = this.pool.pop() ?? [];
      this.cells.set(k, a);
    }
    a.push(o);
  }

  /** Append every point in cells overlapping the AABB to `out`. Points live in
   *  exactly one cell and query cells are disjoint, so `out` has no dupes. */
  collect(xMin: number, yMin: number, xMax: number, yMax: number, out: T[]): void {
    const x0 = Math.floor(xMin / POINT_CELL_M), x1 = Math.floor(xMax / POINT_CELL_M);
    const y0 = Math.floor(yMin / POINT_CELL_M), y1 = Math.floor(yMax / POINT_CELL_M);
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        const a = this.cells.get(pointCellKey(x, y));
        if (a === undefined) continue;
        for (let i = 0; i < a.length; i++) out.push(a[i]);
      }
    }
  }
}

const _itemHash = new PointHash<SpaceItem>();
const _sparkHash = new PointHash<Spark>();
let _maxItemR = 0;

/** dt param avoids tick.ts import cycle. */
export function stepPlumes(world: World, dt: number): void {
  world.plumeSegments.length = 0;
  if (dt <= 0) return;

  // Index item/spark positions once for the whole tick's plume gather.
  _itemHash.clear();
  _maxItemR = 0;
  for (const it of world.items.values()) {
    const r = getItemDef(it.defId).sizeM * 0.5;
    if (r > _maxItemR) _maxItemR = r;
    _itemHash.insert(it);
  }
  _sparkHash.clear();
  const sparks = world.sparks;
  for (let i = 0; i < sparks.length; i++) _sparkHash.insert(sparks[i]);

  for (const ship of world.ships.values()) {
    stepShipPlumes(world, ship, dt);
  }
}

function stepShipPlumes(world: World, ship: Ship, dt: number): void {
  const sr = jitterAngle(ship.body.rot);
  const cs = Math.cos(sr);
  const sn = Math.sin(sr);
  const cell = shipCellSize(ship);

  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    if (!def.thrusters) continue;
    const acts = m.thrusterActivations;
    const eff = effectiveCells(def, m.facing);

    for (let i = 0; i < def.thrusters.length; i++) {
      const a = acts[i];
      if (a < MIN_ACTIVATION) continue;
      const port = def.thrusters[i];

      const shipDir = cardinalVec(rotateCardinal(port.dir, m.facing));
      const exX = -shipDir.x;
      const exY = -shipDir.y;
      const cxLocal = (m.gx + eff.w / 2) * cell - ship.com.x;
      const cyLocal = (m.gy + eff.h / 2) * cell - ship.com.y;
      const halfDepth = (Math.abs(exX) > 0.5 ? eff.w : eff.h) * cell * 0.5;
      const nozzleLocalX = cxLocal + exX * halfDepth;
      const nozzleLocalY = cyLocal + exY * halfDepth;
      const nozzleX = ship.body.pos.x + nozzleLocalX * cs - nozzleLocalY * sn;
      const nozzleY = ship.body.pos.y + nozzleLocalX * sn + nozzleLocalY * cs;

      const exAngleLocal = jitterAngle(Math.atan2(exY, exX));
      const worldAng = jitterAngle(sr + exAngleLocal);
      const wdx = Math.cos(worldAng);
      const wdy = Math.sin(worldAng);

      const fullLen = port.plumeLengthM;
      const maxRayLen = fullLen * a;

      const tipX = nozzleX + wdx * maxRayLen;
      const tipY = nozzleY + wdy * maxRayLen;
      const perp = Math.tan(CONE_HALF_ANGLE) * maxRayLen;
      const fanX = -wdy * perp;
      const fanY =  wdx * perp;
      const xMin = Math.min(nozzleX, tipX + fanX, tipX - fanX);
      const xMax = Math.max(nozzleX, tipX + fanX, tipX - fanX);
      const yMin = Math.min(nozzleY, tipY + fanY, tipY - fanY);
      const yMax = Math.max(nozzleY, tipY + fanY, tipY - fanY);

      // Broadphase via point hash (query expanded by max radius so no candidate
      // is missed); castRay re-tests precisely, so this superset is exact.
      _candItems.length = 0;
      _itemHash.collect(xMin - _maxItemR, yMin - _maxItemR, xMax + _maxItemR, yMax + _maxItemR, _candItems);
      _candSparks.length = 0;
      _sparkHash.collect(
        xMin - SPARK_HIT_RADIUS_M, yMin - SPARK_HIT_RADIUS_M,
        xMax + SPARK_HIT_RADIUS_M, yMax + SPARK_HIT_RADIUS_M, _candSparks,
      );
      _candShips.length = 0;
      for (const other of world.ships.values()) {
        if (other.id === ship.id) continue;
        const ext = shipExtent(other);
        if (other.body.pos.x + ext < xMin || other.body.pos.x - ext > xMax) continue;
        if (other.body.pos.y + ext < yMin || other.body.pos.y - ext > yMax) continue;
        _candShips.push(other);
      }

      const heatPerRay = (port.heatW * a * dt) / BASE_RAYS;
      const impulsePerRay = (port.thrustN * a * dt) / BASE_RAYS;

      for (let k = 0; k < BASE_RAYS; k++) {
        const tri = (Math.random() + Math.random()) - 1;
        const theta = tri * CONE_HALF_ANGLE;
        const ang = worldAng + theta;
        const dx = Math.cos(ang);
        const dy = Math.sin(ang);

        const lenScale = 0.65 + Math.random() * 0.35;
        const rayLen = fullLen * a * lenScale;
        if (rayLen <= 0) continue;

        const hit = castRay(nozzleX, nozzleY, dx, dy, rayLen);
        const usedLen = hit ? hit.t : rayLen;
        if (usedLen > 0) {
          world.plumeSegments.push({
            cx: nozzleX + dx * usedLen * 0.5,
            cy: nozzleY + dy * usedLen * 0.5,
            rot: ang,
            len: usedLen,
            width: RAY_WIDTH_M,
            alongStart: 0,
            alongEnd: usedLen / fullLen,
          });
        }

        if (!hit) continue;

        const distFrac = hit.t / Math.max(fullLen, 0.001);
        const falloff = Math.pow(Math.max(0, 1 - distFrac), PLUME_FALLOFF_EXP);
        const impulseNs = impulsePerRay * falloff;
        const heatJ = heatPerRay * falloff;
        const hitX = nozzleX + dx * hit.t;
        const hitY = nozzleY + dy * hit.t;

        applyHit(hit, hitX, hitY, dx, dy, impulseNs, heatJ);

        const dot = dx * hit.nx + dy * hit.ny;
        const rx = dx - 2 * dot * hit.nx;
        const ry = dy - 2 * dot * hit.ny;
        const childLen = rayLen * CHILD_LEN_FRAC * (0.7 + 0.6 * Math.random());
        if (childLen > 0) {
          const childAng = jitterAngle(Math.atan2(ry, rx));
          const alongStart = hit.t / fullLen;
          const alongEnd = alongStart + childLen / fullLen;
          world.plumeSegments.push({
            cx: hitX + rx * childLen * 0.5,
            cy: hitY + ry * childLen * 0.5,
            rot: childAng,
            len: childLen,
            width: RAY_WIDTH_M,
            alongStart,
            alongEnd,
          });
        }
      }
    }
  }
  _candItems.length = 0;
  _candShips.length = 0;
  _candSparks.length = 0;
}

function applyHit(
  hit: RayHit,
  hitX: number, hitY: number,
  dx: number, dy: number,
  impulseNs: number, heatJ: number,
): void {
  let mass = 0;
  let inertia = 0;
  let posX = 0, posY = 0;
  let vel: { x: number; y: number } | null = null;
  let angHolder: { angVel: number } | null = null;
  let thermal: { tempK: number } | null = null;
  let heatCap = 0;

  if (hit.kind === "item" && hit.item) {
    const it = hit.item;
    const def = getItemDef(it.defId);
    mass = Math.max(0.01, def.stats.mass);
    const r = def.sizeM * 0.5;
    // Solid-disk inertia must match collectItemHandles in physics/step.ts.
    inertia = 0.5 * mass * r * r;
    posX = it.pos.x; posY = it.pos.y;
    vel = it.vel;
    angHolder = it;
    thermal = it;
    heatCap = mass * SPECIFIC_HEAT_J_PER_KG_K;
  } else if (hit.kind === "module" && hit.ship && hit.module) {
    const sh = hit.ship;
    const md = hit.module;
    const mdef = md.def;
    mass = sh.body.mass;
    inertia = sh.body.inertia;
    posX = sh.body.pos.x; posY = sh.body.pos.y;
    vel = sh.body.vel;
    angHolder = sh.body;
    thermal = md;
    heatCap = Math.max(1, mdef.stats.mass) * SPECIFIC_HEAT_J_PER_KG_K;
  } else if (hit.kind === "spark" && hit.spark) {
    const sp = hit.spark;
    mass = Math.max(1e-6, sp.mass);
    posX = sp.pos.x; posY = sp.pos.y;
    vel = sp.vel;
    thermal = sp;
    heatCap = sparkHeatCapJPerK(sp);
  } else {
    return;
  }

  const Jx = dx * impulseNs;
  const Jy = dy * impulseNs;
  vel!.x += Jx / mass;
  vel!.y += Jy / mass;
  if (inertia > 0 && angHolder) {
    const rxC = hitX - posX;
    const ryC = hitY - posY;
    angHolder.angVel += (rxC * Jy - ryC * Jx) / inertia;
  }

  if (thermal && heatCap > 0 && heatJ > 0) {
    thermal.tempK += heatJ / heatCap;
  }
}

function castRay(
  ox: number, oy: number,
  dx: number, dy: number,
  maxT: number,
): RayHit | null {
  let bestT = maxT;
  let hasHit = false;

  for (let i = 0; i < _candItems.length; i++) {
    const it = _candItems[i];
    const r = getItemDef(it.defId).sizeM * 0.5;
    const x0 = it.pos.x - r;
    const y0 = it.pos.y - r;
    const x1 = it.pos.x + r;
    const y1 = it.pos.y + r;
    const t = rayAabbEntry(ox, oy, dx, dy, x0, y0, x1, y1);
    if (t < 0 || t >= bestT) continue;
    bestT = t;
    _scratchHit.t = t;
    _scratchHit.nx = _scratchNx;
    _scratchHit.ny = _scratchNy;
    _scratchHit.kind = "item";
    _scratchHit.item = it;
    _scratchHit.ship = undefined;
    _scratchHit.module = undefined;
    _scratchHit.spark = undefined;
    hasHit = true;
  }

  for (let i = 0; i < _candSparks.length; i++) {
    const sp = _candSparks[i];
    const x0 = sp.pos.x - SPARK_HIT_RADIUS_M;
    const y0 = sp.pos.y - SPARK_HIT_RADIUS_M;
    const x1 = sp.pos.x + SPARK_HIT_RADIUS_M;
    const y1 = sp.pos.y + SPARK_HIT_RADIUS_M;
    const t = rayAabbEntry(ox, oy, dx, dy, x0, y0, x1, y1);
    if (t < 0 || t >= bestT) continue;
    bestT = t;
    _scratchHit.t = t;
    _scratchHit.nx = _scratchNx;
    _scratchHit.ny = _scratchNy;
    _scratchHit.kind = "spark";
    _scratchHit.spark = sp;
    _scratchHit.item = undefined;
    _scratchHit.ship = undefined;
    _scratchHit.module = undefined;
    hasHit = true;
  }

  for (let i = 0; i < _candShips.length; i++) {
    const other = _candShips[i];
    const otherRot = jitterAngle(-other.body.rot);
    const cr = Math.cos(otherRot);
    const sr = Math.sin(otherRot);
    const dx0 = ox - other.body.pos.x;
    const dy0 = oy - other.body.pos.y;
    const localOX = dx0 * cr - dy0 * sr + other.com.x;
    const localOY = dx0 * sr + dy0 * cr + other.com.y;
    const localDX = dx * cr - dy * sr;
    const localDY = dx * sr + dy * cr;
    const otherCell = shipCellSize(other);

    for (const om of other.modules) {
      if (om.hp <= 0) continue;
      const od = om.def;
      const oeff = effectiveCells(od, om.facing);
      const x0 = om.gx * otherCell;
      const y0 = om.gy * otherCell;
      const x1 = x0 + oeff.w * otherCell;
      const y1 = y0 + oeff.h * otherCell;
      const t = rayAabbEntry(localOX, localOY, localDX, localDY, x0, y0, x1, y1);
      if (t < 0 || t >= bestT) continue;
      bestT = t;
      const otherWorldRot = jitterAngle(other.body.rot);
      const wcr = Math.cos(otherWorldRot);
      const wsr = Math.sin(otherWorldRot);
      _scratchHit.t = t;
      _scratchHit.nx = _scratchNx * wcr - _scratchNy * wsr;
      _scratchHit.ny = _scratchNx * wsr + _scratchNy * wcr;
      _scratchHit.kind = "module";
      _scratchHit.ship = other;
      _scratchHit.module = om;
      _scratchHit.item = undefined;
      _scratchHit.spark = undefined;
      hasHit = true;
    }
  }
  return hasHit ? _scratchHit : null;
}

let _scratchNx = 0;
let _scratchNy = 0;

/** Origin-inside-box returns -1: avoids t=0 deposit when nozzle grazes a hitbox. */
function rayAabbEntry(
  ox: number, oy: number, dx: number, dy: number,
  x0: number, y0: number, x1: number, y1: number,
): number {
  let tmin = -Infinity;
  let tmax = Infinity;
  let nxAtMin = 0, nyAtMin = 0;

  if (Math.abs(dx) < 1e-12) {
    if (ox < x0 || ox > x1) return -1;
  } else {
    const inv = 1 / dx;
    let t0 = (x0 - ox) * inv;
    let t1 = (x1 - ox) * inv;
    let nA = -1;
    if (t0 > t1) {
      const tmp = t0; t0 = t1; t1 = tmp;
      nA = 1;
    }
    if (t0 > tmin) { tmin = t0; nxAtMin = nA; nyAtMin = 0; }
    if (t1 < tmax) tmax = t1;
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dy) < 1e-12) {
    if (oy < y0 || oy > y1) return -1;
  } else {
    const inv = 1 / dy;
    let t0 = (y0 - oy) * inv;
    let t1 = (y1 - oy) * inv;
    let nA = -1;
    if (t0 > t1) {
      const tmp = t0; t0 = t1; t1 = tmp;
      nA = 1;
    }
    if (t0 > tmin) { tmin = t0; nxAtMin = 0; nyAtMin = nA; }
    if (t1 < tmax) tmax = t1;
    if (tmin > tmax) return -1;
  }
  if (tmax < 0 || tmin < 0) return -1;
  _scratchNx = nxAtMin;
  _scratchNy = nyAtMin;
  return tmin;
}
